import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MatrixRain from './components/background/MatrixRain';
import ScanLines from './components/background/ScanLines';
import NeuralNetwork from './components/background/NeuralNetwork';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import SolutionsPage from './pages/SolutionsPage';
import ArchitecturePage from './pages/ArchitecturePage';
import SelfHostingPage from './pages/SelfHostingPage';
import DashboardPage from './pages/DashboardPage';
import ResourcesPage from './pages/ResourcesPage';
import LoadingSequence from './components/loading/LoadingSequence';
import { useKonamiCode } from './components/hooks/useKonamiCode';
import { useSecretTyping } from './components/hooks/useSecretTyping';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const [showLoading, setShowLoading] = useState(false);
  const [easterEgg, setEasterEgg] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has visited before (first-time loading sequence)
    const hasVisited = sessionStorage.getItem('macmind_visited');

    if (!hasVisited) {
      setShowLoading(true);
      sessionStorage.setItem('macmind_visited', 'true');

      // Loading sequence duration: ~5 seconds
      setTimeout(() => {
        setShowLoading(false);
      }, 5000);
    }
  }, []);

  // Easter Eggs
  useKonamiCode(() => {
    setEasterEgg('konami');
    setTimeout(() => setEasterEgg(null), 5000);
  });

  useSecretTyping('override', () => {
    setEasterEgg('override');
    setTimeout(() => setEasterEgg(null), 5000);
  });

  const handleLogoEasterEgg = () => {
    setEasterEgg('logo');
    setTimeout(() => setEasterEgg(null), 5000);
  };

  if (showLoading) {
    return <LoadingSequence />;
  }

  return (
    <Router>
      <div className="relative w-full min-h-screen overflow-hidden bg-dark-bg">
        {/* Easter Egg Notifications */}
        <AnimatePresence>
          {easterEgg && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50 glass px-6 py-4 rounded-lg border-2 border-cyan-400 shadow-2xl"
            >
              <div className="flex items-center gap-3">
                <div className="text-3xl">
                  {easterEgg === 'konami' ? '🎮' : easterEgg === 'override' ? '🔓' : '🧠'}
                </div>
                <div>
                  <div className="text-cyan-400 font-bold text-lg">
                    {easterEgg === 'konami'
                      ? 'Konami Code Activated!'
                      : easterEgg === 'override'
                      ? 'System Override Enabled!'
                      : 'Logo Master Unlocked!'}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {easterEgg === 'konami'
                      ? 'You found the secret! Dragons now have +30 power!'
                      : easterEgg === 'override'
                      ? 'Administrator access granted. Full system control unlocked.'
                      : 'You clicked the MacMind logo 10 times! Neural network expanded.'}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Background Effects Layer */}
        <div className="fixed inset-0 z-0">
          <MatrixRain />
          <ScanLines />
          <NeuralNetwork />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/50 via-dark-bg/80 to-dark-bg" />
        </div>

        {/* Navigation */}
        <Navigation />

        {/* Main Content */}
        <div className="relative z-10">
          <Routes>
            <Route path="/" element={<HomePage onLogoEasterEgg={handleLogoEasterEgg} />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/architecture" element={<ArchitecturePage />} />
            <Route path="/self-hosting" element={<SelfHostingPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
